import type { ComponentProps } from "react"
import { cn } from "@/utilities"

interface SkeletonProps extends ComponentProps<"div"> {}

function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-muted", className)}
      {...props}
    />
  )
}

export function ThankYouSkeleton() {
  return (
    <div className='mx-auto w-full max-w-4xl space-y-8 px-6 py-12'>
      {/* Success Hero Skeleton */}
      <div className='flex flex-col items-center gap-4'>
        <Skeleton className='size-16 rounded-full' />
        <div className='space-y-2 text-center'>
          <Skeleton className='mx-auto h-10 w-80' />
          <Skeleton className='mx-auto h-6 w-96' />
        </div>
      </div>

      {/* Grid Skeleton */}
      <div className='grid gap-6 lg:grid-cols-2'>
        {/* Order Summary Skeleton */}
        <div className='space-y-4 rounded-2xl border border-border bg-background p-6 shadow-xs'>
          <div className='space-y-2'>
            <Skeleton className='h-4 w-32' />
            <Skeleton className='h-8 w-40' />
          </div>
          <div className='space-y-2'>
            <Skeleton className='h-4 w-32' />
            <Skeleton className='h-6 w-48' />
          </div>
          <div className='space-y-2'>
            <Skeleton className='h-4 w-32' />
            <Skeleton className='h-6 w-24' />
          </div>
          <div className='border-border border-t pt-4'>
            <div className='flex items-center justify-between'>
              <Skeleton className='h-6 w-16' />
              <Skeleton className='h-8 w-32' />
            </div>
          </div>
        </div>

        {/* Order Items Skeleton */}
        <div className='space-y-4 rounded-2xl border border-border bg-background p-6 shadow-xs'>
          <Skeleton className='h-6 w-32' />
          <div className='space-y-4'>
            {Array.from({ length: 2 }, (_, i) => `skeleton-${i}`).map((key) => (
              <div className='flex gap-4' key={key}>
                <Skeleton className='size-20 shrink-0 rounded-lg' />
                <div className='flex flex-1 flex-col justify-between gap-1'>
                  <Skeleton className='h-5 w-full' />
                  <Skeleton className='h-4 w-24' />
                  <Skeleton className='h-5 w-20' />
                </div>
              </div>
            ))}
          </div>
          <div className='space-y-2 border-border border-t pt-4'>
            <div className='flex justify-between'>
              <Skeleton className='h-4 w-20' />
              <Skeleton className='h-4 w-24' />
            </div>
            <div className='flex justify-between'>
              <Skeleton className='h-4 w-16' />
              <Skeleton className='h-4 w-20' />
            </div>
            <div className='flex justify-between'>
              <Skeleton className='h-6 w-16' />
              <Skeleton className='h-6 w-28' />
            </div>
          </div>
        </div>
      </div>

      {/* Customer Info Skeleton */}
      <div className='space-y-4 rounded-2xl border border-border bg-background p-6 shadow-xs'>
        <Skeleton className='h-6 w-48' />
        <div className='grid gap-6 sm:grid-cols-2'>
          <div className='space-y-3'>
            <Skeleton className='h-5 w-40' />
            <div className='space-y-2'>
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-3/4' />
              <Skeleton className='h-4 w-1/2' />
            </div>
          </div>
          <div className='space-y-3'>
            <Skeleton className='h-5 w-40' />
            <div className='space-y-2'>
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-3/4' />
              <Skeleton className='h-4 w-1/2' />
            </div>
          </div>
        </div>
      </div>

      {/* Actions Skeleton */}
      <div className='flex flex-col gap-3 sm:flex-row'>
        <Skeleton className='h-11 flex-1' />
        <Skeleton className='h-11 w-full sm:w-32' />
        <Skeleton className='h-11 w-full sm:w-48' />
      </div>
    </div>
  )
}
